import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ArrowLeft,
  FileText,
  Download,
  Loader2,
  Trash2,
  CheckCircle,
  XCircle,
} from "lucide-react";

import { db } from "../web/src/firebaseClient";
import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

const COLLECTION_NAME = "researchProjects";

export default function ResearchProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [project, setProject] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const token = await user.getIdTokenResult(true);
        setIsAdmin(
          token.claims.role === "admin" || token.claims.admin === true
        );
      } catch (error) {
        console.error(error);
        setIsAdmin(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);

        const refDoc = doc(db, COLLECTION_NAME, id);
        const snap = await getDoc(refDoc);

        if (!snap.exists()) {
          setProject(null);
          setForm(null);
          return;
        }

        const data = {
          id: snap.id,
          ...snap.data(),
        };

        setProject(data);
        setForm(data);
      } catch (error) {
        console.error("Araştırma projesi alınamadı:", error);
        setProject(null);
        setForm(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id]);

  const handleDelete = async () => {
    if (!isAdmin) return;

    const ok = window.confirm("Bu projeyi silmek istediğine emin misin?");
    if (!ok) return;

    try {
      setActionLoading(true);

      await deleteDoc(doc(db, COLLECTION_NAME, id));

      alert("Proje silindi.");
      navigate(-1);
    } catch (error) {
      console.error(error);
      alert("Silme sırasında hata oluştu.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!isAdmin) return;

    try {
      setActionLoading(true);

      await updateDoc(doc(db, COLLECTION_NAME, id), {
        status: "approved",
        updatedAt: serverTimestamp(),
      });

      setProject((prev) => ({
        ...prev,
        status: "approved",
      }));

      setForm((prev) => ({
        ...prev,
        status: "approved",
      }));

      alert("Proje onaylandı.");
    } catch (error) {
      console.error(error);
      alert("Onaylama sırasında hata oluştu.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!isAdmin) return;

    try {
      setActionLoading(true);

      await updateDoc(doc(db, COLLECTION_NAME, id), {
        status: "rejected",
        updatedAt: serverTimestamp(),
      });

      setProject((prev) => ({
        ...prev,
        status: "rejected",
      }));

      setForm((prev) => ({
        ...prev,
        status: "rejected",
      }));

      alert("Proje reddedildi.");
    } catch (error) {
      console.error(error);
      alert("Reddetme sırasında hata oluştu.");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-[#eef5ff] via-white to-[#fff1f1]">
        <InnovationHubHeader />

        <section className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-white/90 px-6 py-5 text-[#173b7a] shadow-[0_14px_35px_rgba(21,101,192,0.18)]">
            <Loader2 className="animate-spin" size={20} />
            <span className="text-sm font-medium">Yükleniyor...</span>
          </div>
        </section>

        <InnovationHubFooter />
      </main>
    );
  }

  if (!project || !form) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-[#eef5ff] via-white to-[#fff1f1]">
        <InnovationHubHeader />

        <section className="px-4 py-6">
          <div className="mx-auto max-w-[1000px]">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mb-4 flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-[#173b7a] shadow-sm transition hover:bg-white hover:shadow-md"
            >
              <ArrowLeft size={16} />
              Geri
            </button>

            <div className="rounded-2xl border border-blue-100 bg-white/90 p-6 text-sm text-slate-500 shadow-[0_14px_35px_rgba(21,101,192,0.14)]">
              Araştırma projesi bulunamadı.
            </div>
          </div>
        </section>

        <InnovationHubFooter />
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-gradient-to-br from-[#eef5ff] via-white to-[#fff1f1]"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      <InnovationHubHeader />

      <section className="px-3 py-5 sm:px-4 sm:py-6">
        <div className="mx-auto max-w-[1000px]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-4 flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 text-xs font-medium text-[#173b7a] shadow-sm transition hover:bg-white hover:shadow-md sm:text-sm"
          >
            <ArrowLeft size={16} />
            Geri
          </button>

          <article className="overflow-hidden rounded-[24px] bg-gradient-to-br from-[#1565C0] via-white to-[#C62828] p-[1.5px] shadow-[0_18px_50px_rgba(21,101,192,0.22),0_12px_36px_rgba(198,40,40,0.14)] sm:rounded-[28px]">
            <div className="overflow-hidden rounded-[23px] bg-white sm:rounded-[27px]">
              <div className="relative overflow-hidden bg-gradient-to-br from-[#1565C0] via-[#1d74d8] to-[#C62828] px-4 py-6 text-white sm:px-6 sm:py-7">
                <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
                <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

                <div className="relative mb-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-white/20 px-3 py-1 text-[10px] font-semibold shadow-sm backdrop-blur sm:text-xs">
                    Araştırma Çıktısı(White Paper)
                  </span>

                  <span className="rounded-full bg-white/20 px-3 py-1 text-[10px] font-semibold shadow-sm backdrop-blur sm:text-xs">
                    {project.applicantName}
                  </span>
                </div>

                <h1 className="relative text-xl font-semibold leading-snug sm:text-2xl md:text-3xl">
                  {project.projectTitle || "Başlıksız Proje"}
                </h1>
              </div>

              <div className="space-y-4 bg-gradient-to-br from-white via-[#f8fbff] to-[#fff6f6] p-4 sm:space-y-5 sm:p-6">
               
                <InfoBlock
                  title="Amaç"
                  value={project.projectPurpose}
                />

                <InfoBlock
                  title="Özet"
                  value={project.projectSummary}
                />

                <InfoBlock
                  title="Hedef Kitle"
                  value={project.targetAudience}
                />

                <InfoBlock
                  title="Yöntem"
                  value={project.method}
                />

                <InfoBlock
                  title="Sonuç"
                  value={project.result}
                />

                <div className="rounded-2xl border border-blue-100 bg-white/90 p-4 shadow-[0_10px_28px_rgba(21,101,192,0.10)] transition hover:shadow-[0_14px_34px_rgba(21,101,192,0.16)] sm:p-5">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#1565C0]/15 to-[#C62828]/10 text-[#1565C0] shadow-sm sm:h-12 sm:w-12">
                      <FileText size={20} />
                    </div>

                    <div className="min-w-0">
                      <h2 className="text-sm font-semibold text-[#173b7a] sm:text-base">
                        Proje Dokümanı
                      </h2>

                      <p className="truncate text-[11px] text-slate-500 sm:text-xs">
                        {project.projectDocumentName || "PDF dokümanı"}
                      </p>
                    </div>
                  </div>

                  {project.projectDocumentUrl ? (
                    <a
                      href={project.projectDocumentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="inline-flex items-center gap-2 rounded-full bg-[#1565C0] px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-blue-500/20 transition hover:bg-[#0f57aa] hover:shadow-blue-500/30 sm:px-5 sm:text-sm"
                    >
                      <Download size={16} />
                      Dokümanı İndir / Görüntüle
                    </a>
                  ) : (
                    <p className="text-sm text-slate-500">
                      Doküman bağlantısı bulunmuyor.
                    </p>
                  )}
                </div>

                {isAdmin && (
                  <div className="flex flex-wrap justify-end gap-2 border-t border-blue-100 pt-5 sm:gap-3">
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-red-500/20 transition hover:bg-red-700 hover:shadow-red-500/30 disabled:opacity-50 sm:px-5 sm:text-sm"
                    >
                      <Trash2 size={16} />
                      Sil
                    </button>

                    <button
                      type="button"
                      onClick={handleReject}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-full bg-orange-600 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-700 hover:shadow-orange-500/30 disabled:opacity-50 sm:px-5 sm:text-sm"
                    >
                      <XCircle size={16} />
                      Reddet
                    </button>

                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={actionLoading}
                      className="flex items-center gap-2 rounded-full bg-green-600 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-green-500/20 transition hover:bg-green-700 hover:shadow-green-500/30 disabled:opacity-50 sm:px-5 sm:text-sm"
                    >
                      <CheckCircle size={16} />
                      Onayla
                    </button>
                  </div>
                )}
              </div>
            </div>
          </article>
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}

function InfoBlock({ title, value }) {
  return (
    <section className="rounded-2xl border border-blue-100 bg-white/90 p-4 shadow-[0_10px_28px_rgba(21,101,192,0.09)] transition hover:border-blue-200 hover:shadow-[0_14px_34px_rgba(21,101,192,0.14)] sm:p-5">
      <h2 className="mb-2 text-sm font-semibold text-[#173b7a] sm:mb-3 sm:text-base">
        {title}
      </h2>

      <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700 sm:text-[15px] sm:leading-7">
        {value || "Bilgi girilmemiş."}
      </p>
    </section>
  );
}